export interface VehicleIdentifiers {
  vehicleVin?: string | null
  vehicleRegistrationNumber?: string | null
}

export interface UsedVehicle {
  label: string
  vehicleVin: string | null
  color?: string
}

interface CollectUsedVehiclesInput {
  activities: readonly VehicleIdentifiers[]
  estimateActivities: readonly VehicleIdentifiers[]
  vehicleLocations: readonly VehicleIdentifiers[]
  vehicleColors: Record<string, string>
  unknownVehicleLabel: string
}

const clean = (value?: string | null) => value?.trim() || null

export const buildRegistrationNumberByVin = (
  sources: readonly VehicleIdentifiers[]
) => {
  const registrationNumbers: Record<string, string> = {}

  sources.forEach(source => {
    const vin = clean(source.vehicleVin)
    const registrationNumber = clean(source.vehicleRegistrationNumber)

    if (vin && registrationNumber && !registrationNumbers[vin])
      registrationNumbers[vin] = registrationNumber
  })

  return registrationNumbers
}

/**
 * The vehicles listed under the work period map. Tacho activities come first,
 * then the rFMS estimates (which carry a VIN but no plate), and the routes'
 * VINs only when neither names a vehicle. "Unknown vehicle" is listed only
 * when nothing names one, so a stray span without a vehicle does not hide the
 * truck the estimate says was driven.
 */
export const collectUsedVehicles = ({
  activities,
  estimateActivities,
  vehicleLocations,
  vehicleColors,
  unknownVehicleLabel,
}: CollectUsedVehiclesInput): UsedVehicle[] => {
  const registrationNumberByVin = buildRegistrationNumberByVin([
    ...vehicleLocations,
    ...activities,
    ...estimateActivities,
  ])

  const vehicles: UsedVehicle[] = []
  const seen = new Set<string>()
  let hasUnknownVehicle = false

  const addVehicle = (source: VehicleIdentifiers) => {
    const vin = clean(source.vehicleVin)
    const label =
      clean(source.vehicleRegistrationNumber) ??
      (vin ? registrationNumberByVin[vin] : null) ??
      vin

    if (!label) {
      hasUnknownVehicle = true
      return
    }
    if (seen.has(label)) return

    vehicles.push({
      label,
      vehicleVin: vin,
      color: vin ? vehicleColors[vin] : undefined,
    })
    seen.add(label)
  }

  activities.forEach(addVehicle)
  estimateActivities.forEach(addVehicle)
  if (vehicles.length === 0) vehicleLocations.forEach(addVehicle)

  if (vehicles.length === 0 && hasUnknownVehicle)
    vehicles.push({ label: unknownVehicleLabel, vehicleVin: null })

  return vehicles
}
